/**
 * <ProductCard />
 * A reusable grid tile for marketplace & dealer storefronts.
 */
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Folder } from "lucide-react";
import type { Product, SellerRating } from "../../lib/types";
import { Card, CardBody } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { ProductImage } from "../ui/ProductImage";
import { StarRating } from "../reviews/StarRating";
import { api } from "../../lib/api";
import { useLogoContext } from "../../contexts/LogoContext";

// Tiny module-level cache so we don't refetch the same seller rating
// for every card in the grid (a /search returning 24 cards from the
// same dealer would otherwise issue 24 identical requests). Cleared
// implicitly on full page reload, which is fine — ratings update on
// the order-of-minutes, not seconds.
const ratingCache = new Map<string, SellerRating>();

export function ProductCard({ product }: { product: Product }) {
  const navigate = useNavigate();
  const { productPlaceholderUrl } = useLogoContext();
  const hero = product.images?.[0];
  const isFeatured =
    !!product.featured_until && new Date(product.featured_until) > new Date();

  const [rating, setRating] = React.useState<SellerRating | null>(
    ratingCache.get(product.seller_id) ?? null,
  );

  React.useEffect(() => {
    if (!product.seller_id) return;
    const cached = ratingCache.get(product.seller_id);
    if (cached) {
      setRating(cached);
      return;
    }
    let alive = true;
    api
      .get<SellerRating>(`/sellers/${product.seller_id}/rating`)
      .then((r) => {
        ratingCache.set(product.seller_id, r.data);
        if (alive) setRating(r.data);
      })
      .catch(() => {
        // Silent — card still renders without the rating chip.
      });
    return () => {
      alive = false;
    };
  }, [product.seller_id]);

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      aria-label={`View ${product.title}`}
    >
      <Card interactive className="overflow-hidden h-full flex flex-col">
        <div className="aspect-[4/3] bg-surface-sunken relative overflow-hidden">
          <ProductImage
            src={hero}
            alt={product.title}
            loading="lazy"
            className="h-full w-full object-cover"
            placeholderClassName="h-full w-full"
            fallbackSrc={productPlaceholderUrl}
          />
          {isFeatured && (
            <span className="absolute top-3 left-3">
              <Badge variant="brand" size="sm">Featured</Badge>
            </span>
          )}
        </div>

        <CardBody className="flex-1 flex flex-col gap-2">
          <h3 className="font-bold text-ink text-base leading-snug line-clamp-2">
            {product.title}
          </h3>
          {/* Phase 27 — forum category breadcrumb. Absent when the listing
              has no category or it was deleted. */}
          {product.category && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigate(`/forums/c/${product.category!.slug}`);
              }}
              className="appearance-none bg-transparent border-0 p-0 self-start inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink hover:underline truncate max-w-full"
            >
              <Folder className="h-3 w-3 shrink-0" aria-hidden />
              <span className="truncate">
                {product.category.parent_name
                  ? `${product.category.parent_name} › ${product.category.name}`
                  : product.category.name}
              </span>
            </button>
          )}
          <div className="text-xs text-ink-muted font-mono">
            {product.part_number} · {product.condition}
          </div>
          <div className="mt-auto pt-4 flex items-end justify-between gap-3">
            <div>
              <div className="font-display text-2xl font-black text-ink leading-none">
                ${product.price.toFixed(2)}
              </div>
              <div className="text-[11px] text-ink-muted mt-1">
                {product.shipping_type === "FREE"
                  ? "Free shipping"
                  : `+ $${product.shipping_cost.toFixed(2)} ship`}
              </div>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <span className="text-xs font-semibold text-ink-muted truncate">
                @{product.seller_username ?? "seller"}
              </span>
              {/* Phase 17 — only render the chip when the seller has at
                  least one review. "No reviews yet" is too loud on a
                  dense grid; absence is the right default. */}
              {rating && rating.review_count > 0 && (
                <StarRating
                  value={rating.average_rating}
                  count={rating.review_count}
                  size={12}
                />
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}
